import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { imgUrl } from '../utils';
import { useAuth } from '../context/AuthContext';
import './Catalogue.css';

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // Fix #6: input mirrors the URL param on every render (controlled by URL, not local state)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const { isBuyer } = useAuth();

  const activeCategory = searchParams.get('category') || '';
  // Fix #5: committed search term comes from URL, not input state
  const committedSearch = searchParams.get('search') || '';

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  // Fix #5: only fires when URL params change, not on every keystroke
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (committedSearch) params.search = committedSearch;
    api.get('/products', { params }).then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [activeCategory, committedSearch]);

  // Fix #6: keep input in sync when URL changes externally
  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
  }, [searchParams]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(searchParams);
    if (slug) p.set('category', slug); else p.delete('category');
    setSearchParams(p);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput.trim()) p.set('search', searchInput.trim()); else p.delete('search');
    setSearchParams(p);
  };

  // Fix #19: clear search independently
  const clearSearch = () => {
    setSearchInput('');
    const p = new URLSearchParams(searchParams);
    p.delete('search');
    setSearchParams(p);
  };

  const activeParent = categories.find(c => c.slug === activeCategory);
  const activeSub = categories.flatMap(c => c.subcategories || []).find(c => c.slug === activeCategory);

  return (
    <div className="catalogue">
      {/* PAGE HEADER */}
      <div className="catalogue-header">
        <div className="container">
          <p className="tag">ElevenNorth &amp; Co.</p>
          <h1 className="catalogue-title">
            {activeParent ? activeParent.name : activeSub ? activeSub.name : 'Full Catalogue'}
          </h1>
          <p className="catalogue-count">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="container catalogue-body">
        {/* SIDEBAR */}
        <aside className="catalogue-sidebar">
          <form onSubmit={handleSearch} className="search-form">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search products..."
            />
            {committedSearch && (
              <button type="button" className="search-clear" onClick={clearSearch} title="Clear search">✕</button>
            )}
            <button type="submit" className="search-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
          </form>

          <div className="sidebar-section">
            <div className="sidebar-heading">Collections</div>
            <button className={`sidebar-link ${!activeCategory ? 'active' : ''}`} onClick={() => setCategory('')}>All Products</button>
            {categories.map(cat => (
              <div key={cat._id}>
                <button className={`sidebar-link sidebar-parent ${activeCategory === cat.slug ? 'active' : ''}`} onClick={() => setCategory(cat.slug)}>
                  {cat.name}
                </button>
                {cat.subcategories?.map(sub => (
                  <button key={sub._id} className={`sidebar-link sidebar-sub ${activeCategory === sub.slug ? 'active' : ''}`} onClick={() => setCategory(sub.slug)}>
                    {sub.name}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* PRODUCTS */}
        <div className="catalogue-main">
          {!isBuyer && (
            <div className="price-notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span>
                Prices are available to <Link to="/register">Authorised Buyer Accounts</Link> only.{' '}
                <Link to="/register">Register now</Link> or <Link to="/login">log in</Link> to see pricing.
              </span>
            </div>
          )}

          {loading ? (
            <div className="page-loading"><div className="spinner" /></div>
          ) : products.length === 0 ? (
            <div className="no-products">
              <p>No products found.</p>
              <button className="btn-secondary btn-sm" onClick={() => { setCategory(''); clearSearch(); }}>Clear filters</button>
            </div>
          ) : (
            <div className="products-grid">
              {products.map(p => (
                <Link key={p._id} to={`/product/${p._id}`} className="product-card">
                  <div className="product-img">
                    {p.primary_image
                      ? <img src={imgUrl(p.primary_image)} alt={p.name} loading="lazy" />
                      : <div className="product-img-placeholder"><span>Coming Soon</span></div>
                    }
                    {p.stock_status === 'coming_soon' && <div className="product-badge">Coming Soon</div>}
                    {p.featured && p.stock_status !== 'coming_soon' && <div className="product-badge badge-new">New</div>}
                  </div>
                  <div className="product-info">
                    <p className="product-cat">{p.parent_category_name} · {p.category_name}</p>
                    <h3 className="product-name">{p.name}</h3>
                    {p.colours && <p className="product-colours">{p.colours}</p>}
                    <div className="product-footer">
                      <span className="product-moq">Min. {p.min_order_qty} units</span>
                      {p.price_visible
                        ? <span className="product-price">£{Number(p.price).toFixed(2)}</span>
                        : <span className="product-price-locked">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                            Buyer price
                          </span>
                      }
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
